import React, { useRef, useState } from "react";
import { MdDelete, MdFolder, MdOutlineOndemandVideo } from "react-icons/md";
import { FaEdit } from "react-icons/fa";
import { toast } from "react-toastify";

// Recursive helper to get path from root to the selected node
const getPathToNode = (nodes, targetId, path = []) => {
  for (let node of nodes) {
    if (String(node.id) === String(targetId)) return [...path, node.id];
    if (node.subFolders?.length) {
      const subPath = getPathToNode(node.subFolders, targetId, [
        ...path,
        node.id,
      ]);
      if (subPath) return subPath;
    }
  }
  return null;
};

const TreeView = ({
  nodes,
  selectedNodeId,
  currentNodeId,
  onSelectNode,
  onUpdateNode,
  onEditNode,
  onDeleteNode,
  selectable = false, // true only in Add/Edit modals
}) => {
  const [expanded, setExpanded] = useState({});
  const treeRef = useRef(null);

  // Expand all parents whenever selectedNodeId changes
  React.useEffect(() => {
    if (!selectedNodeId) {
      setExpanded({});
      return;
    }

    const path = getPathToNode(nodes, selectedNodeId);
    if (path) {
      const newExpanded = path.reduce(
        (acc, id) => ({ ...acc, [id]: true }),
        {},
      );
      setExpanded(newExpanded);
    }
  }, [selectedNodeId, nodes]);

  // Auto-scroll selected node into view when it changes
  React.useEffect(() => {
    if (!selectedNodeId) return;
    // Defer to allow expansion animation / DOM updates
    const t = setTimeout(() => {
      try {
        const el = treeRef.current?.querySelector(`[data-node-id='${selectedNodeId}']`);
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      } catch (err) {
        // ignore
      }
    }, 80);
    return () => clearTimeout(t);
  }, [selectedNodeId, nodes]);

  const [editingNodeId, setEditingNodeId] = useState(null);

  const toggleExpand = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSelect = (node) => {
    if (!selectable) return;
    if (node.type === "Course") {
      toast.info("Courses are view-only");
      return;
    }
    onSelectNode(node.id);
  };
  return (
    <ul ref={treeRef} className="space-y-1 pl-2">
      {nodes.map((node) => {
        const isSelected = selectedNodeId != null && String(selectedNodeId) === String(node.id);

        return (
          <li key={node.id}>
            <div
              data-node-id={node.id}
              aria-current={isSelected ? "true" : undefined}
              className={`flex items-center justify-between rounded-md px-2 py-1 transition-colors ${
                isSelected
                  ? selectable
                    ? "bg-emerald-500 text-white shadow-sm ring-1 ring-emerald-600"
                    : "bg-blue-100 dark:bg-blue-500 text-white"
                  : "hover:bg-gray-100 dark:hover:bg-gray-300"
              }`}
            >
              <div
                className={`flex items-center w-full ${
                  selectable && node.type === "Folder"
                    ? "cursor-pointer"
                    : "cursor-default"
                }`}
                onClick={() => handleSelect(node)}
              >
                {node.subFolders?.length > 0 ? (
                  <span
                    className="mr-2 text-gray-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleExpand(node.id);
                    }}
                  >
                    {expanded[node.id] ? "▼" : "▶"}
                  </span>
                ) : (
                  <span className="mr-2">
                    {node.subFolders ? (
                      <MdFolder className="text-yellow-500 inline" />
                    ) : (
                      <MdOutlineOndemandVideo className="text-blue-500 inline" />
                    )}
                  </span>
                )}

                {editingNodeId === node.id ? (
                  <input
                    type="text"
                    value={node.name}
                    onChange={(e) => onUpdateNode(node.id, e.target.value)}
                    onBlur={() => setEditingNodeId(null)}
                    autoFocus
                    className="border-b focus:outline-none bg-transparent w-full text-gray-800"
                  />
                ) : (
                  <span
                    className={`flex items-center gap-2 w-full ${
                      node.type === "Course"
                        ? "cursor-default text-gray-500"
                        : ""
                    }`}
                  >
                    {node.name}
                    {currentNodeId != null && String(currentNodeId) === String(node.id) && (
                      <span className="ml-2 text-xs font-semibold opacity-75">Current folder</span>
                    )}
                    {/* {isSelected && (
                      <FaCheckCircle className="text-white text-sm" />
                    )} */}
                  </span>
                )}
              </div>

              {/* Buttons for edit & delete only for folders */}
              {node.type === "Folder" && !selectable && (
                <div className="flex gap-1 ml-2">
                  <button
                    onClick={() => onEditNode(node.id)}
                    className="text-blue-500 hover:text-blue-400"
                  >
                    <FaEdit />
                  </button>
                  <button
                    onClick={() => onDeleteNode(node.id)}
                    className="text-red-500 hover:text-red-400"
                  >
                    <MdDelete />
                  </button>
                </div>
              )}
            </div>

            {expanded[node.id] && node.subFolders?.length > 0 && (
              <div className="ml-4 border-l border-gray-300 pl-3">
                <TreeView
                  nodes={node.subFolders}
                  selectedNodeId={selectedNodeId}
                  currentNodeId={currentNodeId}
                  onSelectNode={onSelectNode}
                  onUpdateNode={onUpdateNode}
                  onEditNode={onEditNode}
                  onDeleteNode={onDeleteNode}
                  selectable={selectable}
                />
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export default TreeView;
// import React, { useState } from "react";
// import { MdDelete, MdFolder, MdOutlineOndemandVideo } from "react-icons/md";
// import { FaEdit, FaCheckCircle } from "react-icons/fa";
// import { toast } from "react-toastify";

// // Recursive helper to get path from root to the selected node
// const getPathToNode = (nodes, targetId, path = []) => {
//   for (let node of nodes) {
//     if (node.id === targetId) return [...path, node.id];
//     if (node.subFolders?.length) {
//       const subPath = getPathToNode(node.subFolders, targetId, [
//         ...path,
//         node.id,
//       ]);
//       if (subPath) return subPath;
//     }
//   }
//   return null;
// };

// const TreeView = ({
//   nodes,
//   selectedNodeId,
//   onSelectNode,
//   onUpdateNode,
//   onEditNode,
//   onDeleteNode,
//   selectable = false, // <--- NEW PROP (true only in Add/Edit modals)
// }) => {
//   const initialExpanded = selectedNodeId
//     ? getPathToNode(nodes, selectedNodeId)
//     : [];
//   const [expanded, setExpanded] = useState(
//     initialExpanded?.reduce((acc, id) => ({ ...acc, [id]: true }), {}) || {}
//   );

//   const [editingNodeId, setEditingNodeId] = useState(null);

//   const toggleExpand = (id) => {
//     setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
//   };

//   const handleSelect = (node) => {
//     if (!selectable) return; // Only allow selection in modals
//     if (node.type === "Course") {
//       toast.info("Courses are view-only");
//       return;
//     }
//     onSelectNode(node.id);
//   };

//   return (
//     <ul className="space-y-1 pl-2">
//       {nodes.map((node) => {
//         const isSelected = selectedNodeId === node.id;

//         return (
//           <li key={node.id}>
//             <div
//               className={`flex items-center justify-between rounded-md px-2 py-1 transition-colors ${
//                 isSelected
//                   ? "bg-blue-100 dark:bg-blue-500 text-white"
//                   : "hover:bg-gray-100 dark:hover:bg-gray-300"
//               }`}
//             >
//               <div
//                 className={`flex items-center w-full ${
//                   selectable && node.type === "Folder"
//                     ? "cursor-pointer"
//                     : "cursor-default"
//                 }`}
//                 onClick={() => handleSelect(node)}
//               >
//                 {node.subFolders?.length > 0 ? (
//                   <span
//                     className="mr-2 text-gray-600"
//                     onClick={(e) => {
//                       e.stopPropagation();
//                       toggleExpand(node.id);
//                     }}
//                   >
//                     {expanded[node.id] ? "▼" : "▶"}
//                   </span>
//                 ) : (
//                   <span className="mr-2">
//                     {node.subFolders ? (
//                       <MdFolder className="text-yellow-500 inline" />
//                     ) : (
//                       <MdOutlineOndemandVideo className="text-blue-500 inline" />
//                     )}
//                   </span>
//                 )}

//                 {editingNodeId === node.id ? (
//                   <input
//                     type="text"
//                     value={node.name}
//                     onChange={(e) => onUpdateNode(node.id, e.target.value)}
//                     onBlur={() => setEditingNodeId(null)}
//                     autoFocus
//                     className="border-b focus:outline-none bg-transparent w-full text-gray-800"
//                   />
//                 ) : (
//                   <span
//                     className={`flex items-center gap-2 w-full ${
//                       node.type === "Course"
//                         ? "cursor-default text-gray-500"
//                         : ""
//                     }`}
//                   >
//                     {node.name}
//                     {/* {isSelected && (
//                       <FaCheckCircle className="text-white text-sm" />
//                     )} */}
//                   </span>
//                 )}
//               </div>

//               {/* Buttons for edit & delete only for folders */}
//               {node.type === "Folder" && !selectable && (
//                 <div className="flex gap-1 ml-2">
//                   <button
//                     onClick={() => onEditNode(node.id)}
//                     className="text-blue-500 hover:text-blue-400"
//                   >
//                     <FaEdit />
//                   </button>
//                   <button
//                     onClick={() => onDeleteNode(node.id)}
//                     className="text-red-500 hover:text-red-400"
//                   >
//                     <MdDelete />
//                   </button>
//                 </div>
//               )}
//             </div>

//             {expanded[node.id] && node.subFolders?.length > 0 && (
//               <div className="ml-4 border-l border-gray-300 pl-3">
//                 <TreeView
//                   nodes={node.subFolders}
//                   selectedNodeId={selectedNodeId}
//                   onSelectNode={onSelectNode}
//                   onUpdateNode={onUpdateNode}
//                   onEditNode={onEditNode}
//                   onDeleteNode={onDeleteNode}
//                   selectable={selectable}
//                 />
//               </div>
//             )}
//           </li>
//         );
//       })}
//     </ul>
//   );
// };

// export default TreeView;
